import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ErrorMessage } from '@hookform/error-message';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody,
  Button
} from 'reactstrap';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';

import LoginConfigurationService from '../service';
import FlexSelect from 'components/Inputs/FlexSelect';
import { useGetProfiles } from '../../User/form/hooks/useGetProfiles';
import { FiArrowDownCircle, FiArrowUpCircle, FiTrash2 } from 'react-icons/fi';

const LoginConfigurationForm = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [authMethod, setAuthMethod] = useState('');
  const [allowDefaultLogin, setAllowDefaultLogin] = useState(true);
  const { profiles } = useGetProfiles();

  const { id } = useParams();
  const { reset, control, handleSubmit, errors, setValue, getValues } = useForm(
    {
      shouldUnregister: false
    }
  );
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'groupProfile',
    rules: {
      required: 'Informe o nome do grupo e o perfil de acesso'
    },
    defaultValue: [{ groupName: '', profileId: '' }]
  });

  useEffect(() => {
    setLoading(true);
    LoginConfigurationService.getLoginConfigurations()
      .then((res) => {
        const config = res.data;
        const authMethodValue = config.isAzureADldapActive ? 'AzureAD' : 'SSO';
        setAuthMethod(authMethodValue);
        setAllowDefaultLogin(config.allowDefaultLogin);
        reset({ groupProfile: config.groupProfile });
        reset(config);
        setValue('authMethod', authMethodValue);
      })
      .catch((error) => {
        const errorMsg = error.response?.data?.message;
        toast.error(errorMsg || 'Erro ao buscar configurações de login');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, reset, setValue]);

  const onSubmit = async (data) => {
    try {
      setIsSubmiting(true);

      const formatedGroupProfile = data.groupProfile.map((item, index) => ({
        groupName: item.groupName.trim(),
        profileId: item.profileId,
        order: index + 1
      }));

      const hasEmptyField = formatedGroupProfile.some(
        (item) => !item.groupName || !item.profileId
      );

      if (hasEmptyField) {
        toast.error(
          'Preencha todos os campos de nome do grupo e do perfil de acesso',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
        return;
      }

      const formatedData = {
        isAzureADldapActive: authMethod === 'AzureAD',
        isSSOEnabled: authMethod === 'SSO',
        groupProfile: formatedGroupProfile || []
      };

      if (authMethod === 'AzureAD') {
        formatedData.allowDefaultLogin = allowDefaultLogin;
        formatedData.url = data.url;
        formatedData.baseDN = data.baseDN;
      } else if (authMethod === 'SSO') {
        formatedData.tenantId = data.tenantId;
        formatedData.clientSecret = data.clientSecret;
        formatedData.clientId = data.clientId;
        formatedData.redirectUrl = data.redirectUrl;
      }

      await LoginConfigurationService.saveLoginConfigurations(formatedData);

      toast.success('Configuração de login salva com sucesso');
    } catch (err) {
      const msg = err.response?.data?.message;
      toast.error(msg || 'Erro ao salvar configuração de login', {
        autoClose: 3000,
        closeOnClick: true
      });
    } finally {
      setIsSubmiting(false);
    }
  };

  const moveUp = (index) => {
    if (index > 0) {
      const updatedFields = [...fields];
      [updatedFields[index], updatedFields[index - 1]] = [
        updatedFields[index - 1],
        updatedFields[index]
      ];
      const previousValue = getValues();
      reset({ groupProfile: updatedFields });
      setValue('url', previousValue.url);
      setValue('baseDN', previousValue.baseDN);
      setValue('tenantId', previousValue.tenantId);
      setValue('clientSecret', previousValue.clientSecret);
      setValue('clientId', previousValue.clientId);
      setValue('redirectUrl', previousValue.redirectUrl);
      setValue('authMethod', authMethod);
    }
  };

  const moveDown = (index) => {
    if (index < fields.length - 1) {
      const updatedFields = [...fields];
      [updatedFields[index], updatedFields[index + 1]] = [
        updatedFields[index + 1],
        updatedFields[index]
      ];
      const previousValue = getValues();
      reset({ groupProfile: updatedFields });
      setValue('url', previousValue.url);
      setValue('baseDN', previousValue.baseDN);
      setValue('tenantId', previousValue.tenantId);
      setValue('clientSecret', previousValue.clientSecret);
      setValue('clientId', previousValue.clientId);
      setValue('redirectUrl', previousValue.redirectUrl);
      setValue('authMethod', authMethod);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/configuration');
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/configuration"
        buttonTitle="Voltar para a pagina de configurações"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-3">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Configurações de Login</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit, (err) =>
                        console.log(err)
                      )}
                    >
                      <Row className="mb-3">
                        <Col md="12">
                          <label className="form-control-label">
                            Método de Autenticação
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                isClearable={false}
                                dataOptions={[
                                  {
                                    id: 'AzureAD',
                                    name: 'Ativar login com Azure AD (conexão LDAP)'
                                  },
                                  { id: 'SSO', name: 'Ativar SSO' }
                                ]}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                value={props.value}
                                valueController={setValue}
                                fieldName="authMethod"
                                labelName="name"
                                valueName="id"
                                onChange={(selected) => {
                                  setAuthMethod(selected);
                                  props.onChange(selected);
                                }}
                              />
                            )}
                            control={control}
                            name="authMethod"
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="authMethod"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      {authMethod === 'AzureAD' && (
                        <>
                          <Row className="mb-3">
                            <Col md="6">
                              <label className="form-control-label">
                                URL{authMethod === 'AzureAD' ? '*' : ''}
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="url"
                                placeholder="ldap://mycompany.com.br:389"
                                disabled={authMethod !== 'AzureAD'}
                                rules={{
                                  required:
                                    authMethod === 'AzureAD'
                                      ? 'Campo obrigatório'
                                      : false,
                                  maxLength: 255
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="url"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col md="6">
                              <label className="form-control-label">
                                DN base{authMethod === 'AzureAD' ? '*' : ''}
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="baseDN"
                                placeholder="dc=mycompany,dc=com,dc=br"
                                disabled={authMethod !== 'AzureAD'}
                                rules={{
                                  required:
                                    authMethod === 'AzureAD'
                                      ? 'Campo obrigatório'
                                      : false,
                                  maxLength: 255
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="baseDN"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col md="12">
                              <label
                                className="form-control-label mb-0"
                                style={{ marginLeft: '-15px' }}
                              >
                                <ToggleSwitch
                                  checked={allowDefaultLogin}
                                  id="allow-default-login"
                                  onChange={() =>
                                    setAllowDefaultLogin((prev) => !prev)
                                  }
                                />
                                Permitir login padrão do FlexUC
                              </label>
                              <br />
                              <span className="small">
                                Isso permite que seja utilizado um usuário local
                                do FlexUC para logar. <br />
                                Caso essa configuração não seja ativada, o
                                acesso fica restrito apenas pelo Azure AD.{' '}
                                <br />
                              </span>
                            </Col>
                          </Row>
                        </>
                      )}
                      {authMethod === 'SSO' && (
                        <>
                          <Row className="mb-3">
                            <Col md="6">
                              <label className="form-control-label">
                                Tenant ID{authMethod === 'SSO' ? '*' : ''}
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="tenantId"
                                placeholder="Tenant ID"
                                disabled={authMethod !== 'SSO'}
                                rules={{
                                  required:
                                    authMethod === 'SSO'
                                      ? 'Campo obrigatório'
                                      : false,
                                  maxLength: 255
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="tenantId"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col md="6">
                              <label className="form-control-label">
                                Client Secret{authMethod === 'SSO' ? '*' : ''}
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="clientSecret"
                                placeholder="Client Secret"
                                disabled={authMethod !== 'SSO'}
                                rules={{
                                  required:
                                    authMethod === 'SSO'
                                      ? 'Campo obrigatório'
                                      : false,
                                  maxLength: 255
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="clientSecret"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col md="6">
                              <label className="form-control-label">
                                Client ID{authMethod === 'SSO' ? '*' : ''}
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="clientId"
                                placeholder="Client ID"
                                disabled={authMethod !== 'SSO'}
                                rules={{
                                  required:
                                    authMethod === 'SSO'
                                      ? 'Campo obrigatório'
                                      : false,
                                  maxLength: 255
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="clientId"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col md="6">
                              <label className="form-control-label">
                                Redirect URL{authMethod === 'SSO' ? '*' : ''}
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name="redirectUrl"
                                placeholder="Redirect URL"
                                disabled={authMethod !== 'SSO'}
                                rules={{
                                  required:
                                    authMethod === 'SSO'
                                      ? 'Campo obrigatório'
                                      : false,
                                  maxLength: 255
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="redirectUrl"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                          </Row>
                        </>
                      )}

                      <div className="pt-3 pb-2 d-flex justify-content-between align-items-center">
                        <div>
                          <Button
                            color="primary"
                            title="Adicionar um Grupo"
                            size="sm"
                            onClick={() =>
                              append({ groupName: '', profileId: '' })
                            }
                          >
                            + Adicionar Grupo
                          </Button>
                        </div>
                      </div>

                      <>
                        {fields.map((field, index) => (
                          <Row className="mb-12" key={field.id}>
                            <Col md="6" className="mt-1">
                              <label className="form-control-label">
                                Nome do Grupo
                              </label>
                              <Controller
                                as={Input}
                                control={control}
                                name={`groupProfile[${index}].groupName`}
                                defaultValue={field.groupName || ''}
                              />
                              <ErrorMessage
                                errors={errors}
                                name={`groupProfile[${index}].groupName`}
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col
                              md={fields.length > 1 ? '5' : '6'}
                              className="mt-1"
                            >
                              <label className="form-control-label">
                                Perfil de Acesso
                              </label>
                              <Controller
                                render={(props) => (
                                  <FlexSelect
                                    isClearable={true}
                                    dataOptions={profiles}
                                    isMulti={false}
                                    closeMenuOnSelect={true}
                                    value={props.value}
                                    valueController={setValue}
                                    fieldName={`groupProfile[${index}].profileId`}
                                    labelName="name"
                                    valueName="id"
                                  />
                                )}
                                control={control}
                                name={`groupProfile[${index}].profileId`}
                                defaultValue={field.profileId || ''}
                              />
                              <ErrorMessage
                                errors={errors}
                                name={`groupProfile[${index}].profileId`}
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            {fields.length > 1 && (
                              <Col
                                md="1"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  marginTop: '30px',
                                  paddingLeft: '0px',
                                  paddingRight: '0px'
                                }}
                              >
                                <>
                                  <FiArrowUpCircle
                                    size={30}
                                    cursor="pointer"
                                    onClick={() => moveUp(index)}
                                  />
                                  <FiArrowDownCircle
                                    style={{ marginLeft: '10px' }}
                                    size={30}
                                    cursor="pointer"
                                    onClick={() => moveDown(index)}
                                  />
                                  <FiTrash2
                                    type="button"
                                    style={{ marginLeft: '10px' }}
                                    size={30}
                                    cursor="pointer"
                                    onClick={() => remove(index)}
                                  />
                                </>
                              </Col>
                            )}
                          </Row>
                        ))}
                        {errors.groupProfile && (
                          <p style={{ color: 'red' }}>
                            {errors.groupProfile.message}
                          </p>
                        )}
                      </>

                      <hr />

                      <ReactButton
                        btnColor="confirmation"
                        type="submit"
                        disabled={isSubmiting}
                      >
                        Salvar
                      </ReactButton>

                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export { LoginConfigurationForm };
